import {NotificationInteraction, type NotificationInteractionData} from "@/types/site_admin/notificationInteraction";
import {Contact} from "@/types/contacts/contact";

export class SiteNotification {
    public id: number | null;
    public admin_name: string | null;
    public client_name: string | null;
    public admin_message: string | null;
    public client_message: string | null;
    public admin_email_text: string | null;
    public client_email_text: string | null;
    public admin_email_subject: string | null;
    public client_email_subject: string | null;
    public admin_enabled: boolean;
    public client_enabled: boolean;
    public admin_sent_notification: boolean;
    public client_sent_notification: boolean;
    public send_admin_email: number;
    public send_client_email: number;
    public admin_force_show: number;
    public client_force_show: number;
    public set_notification_klg: number;
    public set_notification_customer: number;
    public admin_from: Date | null;
    public client_from: Date | null;
    public admin_until: Date | null;
    public client_until: Date | null;
    public admin_send_notification_at: Date | null;
    public client_send_notification_at: Date | null;
    public business_lines: Array<string>;
    public notificationInteractions: NotificationInteraction[];

    constructor(data: SiteNotificationData | void) {
        if (data === undefined) {
            this.id = null;
            this.admin_name = null;
            this.client_name = null;
            this.admin_message = null;
            this.client_message = null;
            this.admin_email_text = null;
            this.client_email_text = null;
            this.admin_email_subject = null;
            this.client_email_subject = null;
            this.admin_enabled = false;
            this.client_enabled = false;
            this.admin_sent_notification = false;
            this.client_sent_notification = false;
            this.send_admin_email = 0;
            this.send_client_email = 0;
            this.admin_force_show = 0;
            this.client_force_show = 0;
            this.set_notification_klg = 0;
            this.set_notification_customer = 0;
            this.admin_from = null;
            this.client_from = null;
            this.admin_until = null;
            this.client_until = null;
            this.admin_send_notification_at = null;
            this.client_send_notification_at = null;
            this.business_lines = [];
            this.notificationInteractions = [];
        } else {
            this.id = data.id;
            this.admin_name = data.admin_name;
            this.client_name = data.client_name;
            this.admin_message = data.admin_message;
            this.client_message = data.client_message;
            this.admin_email_text = data.admin_email_text;
            this.client_email_text = data.client_email_text;
            this.admin_email_subject = data.admin_email_subject;
            this.client_email_subject = data.client_email_subject;
            this.admin_enabled = data.admin_enabled;
            this.client_enabled = data.client_enabled;
            this.admin_sent_notification = data.admin_sent_notification;
            this.client_sent_notification = data.client_sent_notification;
            this.send_admin_email = data.send_admin_email;
            this.send_client_email = data.send_client_email;
            this.admin_force_show = data.admin_force_show;
            this.client_force_show = data.client_force_show;
            this.set_notification_klg = data.set_notification_klg;
            this.set_notification_customer = data.set_notification_customer;

            this.admin_from = null;
            if (data.admin_from !== null) {
                this.admin_from = new Date(data.admin_from);
            }

            this.client_from = null;
            if (data.client_from !== null) {
                this.client_from = new Date(data.client_from);
            }

            this.admin_until = null;
            if (data.admin_until !== null) {
                this.admin_until = new Date(data.admin_until);
            }

            this.client_until = null;
            if (data.client_until !== null) {
                this.client_until = new Date(data.client_until);
            }

            this.admin_send_notification_at = null;
            if (data.admin_send_notification_at !== null && data.admin_send_notification_at !== undefined) {
                this.admin_send_notification_at = new Date(data.admin_send_notification_at);
            }

            this.client_send_notification_at = null;
            if (data.client_send_notification_at !== null && data.client_send_notification_at !== undefined) {
                this.client_send_notification_at = new Date(data.client_send_notification_at);
            }

            this.business_lines = [];
            if(data.business_lines !== null && data.business_lines !== undefined){
                this.business_lines = data.business_lines;
            }

            this.notificationInteractions = [];
            if (data.notificationInteractions !== undefined) {
                data.notificationInteractions.forEach(notificationInteraction => this.notificationInteractions.push((new NotificationInteraction(notificationInteraction))));
            }
        }
    }

    serializeData(without_relations: boolean | Array<string> = false) {
        const serialized_data =  {
            id: this.id,
            admin_name: this.admin_name,
            client_name: this.client_name,
            admin_message: this.admin_message,
            client_message: this.client_message,
            admin_email_text: this.admin_email_text,
            client_email_text: this.client_email_text,
            admin_email_subject: this.admin_email_subject,
            client_email_subject: this.client_email_subject,
            admin_enabled: this.admin_enabled,
            client_enabled: this.client_enabled,
            admin_sent_notification: this.admin_sent_notification,
            client_sent_notification: this.client_sent_notification,
            send_admin_email: this.send_admin_email,
            send_client_email: this.send_client_email,
            admin_force_show: this.admin_force_show,
            client_force_show: this.client_force_show,
            admin_from: this.admin_from,
            client_from: this.client_from,
            admin_until: this.admin_until,
            client_until: this.client_until,
            admin_send_notification_at: this.admin_send_notification_at,
            client_send_notification_at: this.client_send_notification_at,
            set_notification_klg: this.set_notification_klg,
            set_notification_customer: this.set_notification_customer,
            business_lines: this.business_lines,
        }
        if (without_relations === false ||
            (
                Array.isArray(without_relations) &&
                !without_relations.includes('notificationInteractions')
            )
        ) {
            serialized_data.notificationInteractions = [];
            this.notificationInteractions.forEach(el => {
                serialized_data.notificationInteractions.push(el.serializeData())
            })
        }

        return serialized_data;
    }

    public resetToDefaults(): void {
        this.id = null;
        this.admin_name = null;
        this.client_name = null;
        this.admin_message = null;
        this.client_message = null;
        this.admin_email_text = null;
        this.client_email_text = null;
        this.admin_email_subject = null;
        this.client_email_subject = null;
        this.admin_sent_notification = false;
        this.client_sent_notification = false;
        this.admin_enabled = false;
        this.client_enabled = false;
        this.send_admin_email = 0;
        this.send_client_email = 0;
        this.admin_force_show = 0;
        this.client_force_show = 0;
        this.set_notification_klg = 0;
        this.set_notification_customer = 0;
        this.admin_from = null;
        this.client_from = null;
        this.admin_until = null;
        this.client_until = null;
        this.admin_send_notification_at = null;
        this.client_send_notification_at = null;
    }
}

export type SiteNotificationData = {
    id: number | null;
    admin_name: string | null;
    client_name: string | null;
    admin_message: string | null;
    client_message: string | null;
    admin_email_text: string | null;
    client_email_text: string | null;
    admin_email_subject: string | null;
    client_email_subject: string | null;
    admin_enabled: boolean;
    client_enabled: boolean;
    admin_sent_notification: boolean;
    client_sent_notification: boolean;
    send_admin_email: number;
    send_client_email: number;
    admin_force_show: number;
    client_force_show: number;
    set_notification_klg: number;
    set_notification_customer: number;
    admin_from: Date | null;
    client_from: Date | null;
    admin_until: Date | null;
    client_until: Date | null;
    admin_send_notification_at: Date | null;
    client_send_notification_at: Date | null;
    business_lines: Array<string>|null|undefined;
    notificationInteractions: NotificationInteractionData[];
}
